import axios from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ChatItem } from '../../models/chat'
import {
  type DotEditPrefill,
  type InsuranceApplication,
  lookupDotForEdit,
  useEditDotNumberMutation,
  useInsuranceApplicationUserId,
} from '../../models/insuranceApplication'

interface EditDotNumberDialogProps {
  application: InsuranceApplication
  chatItem: ChatItem | null
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

type Step = 'dot' | 'details'

export function EditDotNumberDialog({
  application,
  chatItem,
  onOpenChange,
  onSuccess,
}: EditDotNumberDialogProps) {
  const currentDot =
    (chatItem?.editableAction as { currentDot?: string } | null | undefined)?.currentDot ?? ''
  const initialDot = currentDot
  const [step, setStep] = useState<Step>('dot')
  const [dot, setDot] = useState(initialDot)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const [prefill, setPrefill] = useState<DotEditPrefill | null>(null)
  const { mutateAsync: save, isPending: saving } = useEditDotNumberMutation({ application })
  const userId = useInsuranceApplicationUserId()

  if (!chatItem) return null

  async function handleContinue() {
    setLookupError(null)
    setLooking(true)
    try {
      const data = await lookupDotForEdit(application.id, dot, userId)
      setPrefill(data)
      setStep('details')
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setLookupError("We couldn't find a business for that DOT number.")
      } else {
        setLookupError('Something went wrong looking up that DOT number.')
      }
    } finally {
      setLooking(false)
    }
  }

  async function handleSave() {
    if (!prefill || !chatItem) return
    try {
      await save({ ...prefill, dotNumber: dot, chatItemId: chatItem.id })
      toast.success('DOT number updated')
      onSuccess()
      onOpenChange(false)
    } catch {
      toast.error('Failed to update DOT number')
    }
  }

  function updatePrefill<K extends keyof DotEditPrefill>(key: K, value: string) {
    setPrefill((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  return (
    <Dialog open={!!chatItem} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{step === 'dot' ? 'Edit DOT number' : 'Confirm business details'}</DialogTitle>
        </DialogHeader>

        {step === 'dot' ? (
          <div className="mt-2 flex flex-col gap-3">
            <div>
              <Label htmlFor="edit-dot-number" className="text-sm">
                DOT number
              </Label>
              <Input
                id="edit-dot-number"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="off"
                maxLength={8}
                value={dot}
                onChange={(e) => setDot(e.target.value.replace(/\D/g, '').slice(0, 8))}
                disabled={looking}
              />
            </div>
            {lookupError && <p className="text-sm text-red-600">{lookupError}</p>}
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleContinue} disabled={!dot || looking}>
                {looking ? 'Looking up…' : 'Continue'}
              </Button>
            </div>
          </div>
        ) : (
          prefill && (
            <div className="mt-2 flex flex-col gap-3">
              <p className="text-sm text-gray-600">DOT number: {dot}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label="Business name"
                  value={prefill.businessName}
                  onChange={(v) => updatePrefill('businessName', v)}
                  span2
                />
                <Field
                  label="Address"
                  value={prefill.address1}
                  onChange={(v) => updatePrefill('address1', v)}
                  span2
                />
                <Field label="City" value={prefill.city} onChange={(v) => updatePrefill('city', v)} />
                <Field
                  label="State"
                  value={prefill.state}
                  onChange={(v) => updatePrefill('state', v.toUpperCase().slice(0, 2))}
                />
                <Field label="ZIP" value={prefill.zip} onChange={(v) => updatePrefill('zip', v)} />
                <Field
                  label="Owner phone"
                  value={prefill.ownerPhone}
                  onChange={(v) => updatePrefill('ownerPhone', v)}
                />
                <Field
                  label="Owner first name"
                  value={prefill.ownerFirstName}
                  onChange={(v) => updatePrefill('ownerFirstName', v)}
                />
                <Field
                  label="Owner last name"
                  value={prefill.ownerLastName}
                  onChange={(v) => updatePrefill('ownerLastName', v)}
                />
                <Field
                  label="Owner email"
                  value={prefill.ownerEmail}
                  onChange={(v) => updatePrefill('ownerEmail', v)}
                  span2
                />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setStep('dot')} disabled={saving}>
                  Back
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (value: string) => void
  span2?: boolean
}

function Field({ label, value, onChange, span2 }: FieldProps) {
  return (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <Label className="text-sm">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}
